/**
 * Helpers de upload: validacao client + XHR com progress.
 *
 * Validacao no client serve apenas para feedback rapido -- o backend
 * tem checks identicos (MIME whitelist + size limit) e e fonte da
 * verdade.
 */

import { env } from "@/lib/env";
import { tokenStorage } from "@/auth/tokenStorage";

export const MAX_UPLOAD_SIZE_BYTES = 100 * 1024 * 1024;

/**
 * Whitelist client-side -- espelha
 * ``src/core/config.py::AttachmentsSettings.ALLOWED_MIME_TYPES`` do
 * backend. Backend eh fonte da verdade (415 se diferir), aqui eh so
 * para feedback rapido. Quando ampliar no backend, ampliar aqui tambem.
 */
export const ALLOWED_MIME_TYPES = new Set<string>([
  // --- Documentos --------------------------------------------------
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/rtf",
  "text/rtf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.presentation",
  // --- Planilhas / Dados ------------------------------------------
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.oasis.opendocument.spreadsheet",
  "text/csv",
  "text/tab-separated-values",
  "application/json",
  "application/xml",
  "text/xml",
  "application/x-yaml",
  "text/yaml",
  // --- Texto / Codigo ---------------------------------------------
  "text/plain",
  "text/markdown",
  "text/html",
  "text/css",
  "application/x-javascript",
  "application/javascript",
  "text/javascript",
  "text/x-python",
  "application/x-python",
  "text/x-typescript",
  "application/typescript",
  "text/x-c",
  "text/x-c++",
  "text/x-csharp",
  "text/x-java-source",
  "text/x-go",
  "text/x-rust",
  "text/x-kotlin",
  "text/x-swift",
  "text/x-ruby",
  "text/x-php",
  "application/x-sh",
  "text/x-shellscript",
  "application/sql",
  "text/x-sql",
  // --- Imagens ----------------------------------------------------
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
  "image/bmp",
  "image/svg+xml",
  "image/tiff",
  // --- Audio ------------------------------------------------------
  "audio/wav",
  "audio/x-wav",
  "audio/mpeg",
  "audio/mp3",
  "audio/mp4",
  "audio/m4a",
  "audio/x-m4a",
  "audio/aac",
  "audio/aiff",
  "audio/x-aiff",
  "audio/ogg",
  "audio/opus",
  "audio/flac",
  "audio/webm",
  // --- Video ------------------------------------------------------
  "video/mp4",
  "video/mpeg",
  "video/mpg",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-flv",
  "video/webm",
  "video/x-ms-wmv",
  "video/3gpp",
  "video/3gpp2",
]);

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export interface ValidationResult {
  ok: boolean;
  error?: string;
}

export function validateFile(file: File): ValidationResult {
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    return {
      ok: false,
      error: `Arquivo excede ${formatBytes(MAX_UPLOAD_SIZE_BYTES)}.`,
    };
  }
  const mime = file.type || "application/octet-stream";
  if (!ALLOWED_MIME_TYPES.has(mime)) {
    return {
      ok: false,
      error: `Tipo nao suportado: ${mime}.`,
    };
  }
  return { ok: true };
}

export interface XhrUploadResult {
  status: number;
  body: unknown;
}

/**
 * Upload via XMLHttpRequest para ter callback de progresso. ``fetch``
 * nao expoe ``upload.onprogress`` cross-browser.
 */
export function uploadDocumentXhr(
  kbId: string,
  file: File,
  options: {
    onProgress?: (pct: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<XhrUploadResult> {
  return new Promise(async (resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = `${env.API_BASE_URL}/knowledge-bases/${kbId}/documents`;
    const form = new FormData();
    form.append("file", file);

    xhr.open("POST", url);
    const token = await tokenStorage.getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && options.onProgress) {
        options.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status, body });
      } else {
        reject({ status: xhr.status, body });
      }
    };
    xhr.onerror = () => reject({ status: 0, body: "network_error" });
    xhr.onabort = () => reject({ status: 0, body: "aborted" });

    if (options.signal) {
      if (options.signal.aborted) {
        xhr.abort();
      } else {
        options.signal.addEventListener("abort", () => xhr.abort());
      }
    }

    xhr.send(form);
  });
}
